import { defineStore } from 'pinia'
import { ref } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { APIP2ECoinsInfo, CategoryShortInfo, CategoryType, MainCategoriesType } from '@/types'
import { shuffle } from '@/utils/helpers'
import { getGamesCategoriesShortInfo, getMainCategories } from '@/utils/requests/games'

export type ShortCategory = {
    id: MainCategoriesType
    title: string
    location: RouteLocationRaw
}

const MAX_PROMO_CATEGORIES = 7

export const HIGHLIGHTED_CATEGORY_TYPES: MainCategoriesType[] = ['trending_now', 'new']

export const useGeneralStore = defineStore('general', () => {
    const menuHighlightCategoriesList = ref<ShortCategory[]>([])
    const contentCategoriesList = ref<ShortCategory[]>([])
    const menuCategoriesList = ref<ShortCategory[]>([])
    const promoCategoriesList = ref<ShortCategory[]>([])
    const p2eBonusInfo = ref<APIP2ECoinsInfo>()

    const categoriesInfo = ref<Record<CategoryType, CategoryShortInfo[]>>(
        {} as Record<CategoryType, CategoryShortInfo[]>,
    )
    const loadedLocale = ref<string | null>(null)

    const { getCategoryRoute } = useUrl()
    const i18nAddon = useI18nAddon()

    async function setCommonInfo(locale: string): Promise<void> {
        const data = await getMainCategories(locale)

        loadedLocale.value = locale
        const notInternal: ShortCategory[] = []
        const internalCategories: ShortCategory[] = []
        const { categories, p2e } = data
        p2eBonusInfo.value = p2e.coinsInfo
        categories.forEach(({ id, hru, title }) => {
            const mainCategoryId = id as MainCategoriesType
            if (HIGHLIGHTED_CATEGORY_TYPES.includes(mainCategoryId)) {
                internalCategories.push({
                    id: mainCategoryId,
                    title,
                    location: getCategoryRoute({ hru, type: 'category' }),
                })
            } else {
                notInternal.push({
                    id: mainCategoryId,
                    title,
                    location: getCategoryRoute({ hru, type: 'category' }),
                })
            }
        })

        contentCategoriesList.value = [...internalCategories, ...notInternal]
        menuCategoriesList.value = notInternal
        promoCategoriesList.value = shuffle(notInternal.slice()).slice(
            0,
            Math.min(MAX_PROMO_CATEGORIES, notInternal.length),
        )
        menuHighlightCategoriesList.value = internalCategories
    }
    async function setCategoriesInfo(type: CategoryType): Promise<void> {
        const { currentLocale } = i18nAddon
        const result = await getGamesCategoriesShortInfo(type, currentLocale.value)
        categoriesInfo.value[type] = result.map((catInfo) => ({
            ...catInfo,
            href: getCategoryRoute({ hru: catInfo.hru, type }),
        }))
    }

    // For Easy shuffling on UI side - as we have cache on ssr we have to reshuffle after page rendered
    async function setMainData() {
        const { currentLocale } = i18nAddon
        const localeMatch = currentLocale.value === loadedLocale.value

        if (!contentCategoriesList.value.length || !localeMatch) {
            await setCommonInfo(currentLocale.value)
        }
    }

    return {
        menuHighlightCategoriesList,
        menuCategoriesList,
        promoCategoriesList,
        contentCategoriesList,
        categoriesInfo,
        p2eBonusInfo,
        setMainData,
        setCategoriesInfo,
        // expose to capture on server and hydrate on client
        loadedLocale,
    }
})
